// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { Extension, Organization } from '@medplum/fhirtypes';
import { ext } from '../config/tenant';

// Extension URLs for different settings categories
// All URLs are derived from tenant configuration for multi-tenancy support
export const EXTENSION_URLS = {
  // Practice Settings
  organizationLogo: ext('organization-logo'),

  // AI Notes Settings
  aiNotesSettings: ext('ai-notes-settings'),

  // Appointment Settings
  appointmentSettings: ext('appointment-settings'),
  automaticReminders: ext('automatic-reminders'),

  // Working Hours Settings
  workingHoursSettings: ext('working-hours-settings'),
  breakHours: ext('break-hours'),

  // Cancellation Reasons
  cancellationReasons: ext('cancellation-reasons'),

  // Email Settings
  emailSettings: ext('email-settings'),

  // SMS Settings
  smsSettings: ext('sms-settings'),

  // GDPR/Cookie Settings
  gdprSettings: ext('gdpr-settings'),

  // Measurement Settings (Metric/Imperial)
  measurementSettings: ext('measurement-settings'),

  // Finance Settings
  financeSettings: ext('finance-settings'),

  // App Settings
  appSettings: ext('app-settings'),

  // Email Templates
  emailTemplates: ext('email-templates'),

  // SMS Templates
  smsTemplates: ext('sms-templates'),

  // Form Settings (Organization-level form configuration)
  formSettings: ext('form-settings'),

  // CareComm Settings (Omnichannel communication)
  careCommSettings: ext('carecomm-settings'),
} as const;

// ============================================
// CareComm Communication Extension URLs
// ============================================
// These are used on Communication resources, not Organization
export const CARECOMM_EXTENSION_URLS = {
  twilioMessageSid: ext('twilio-message-sid'),
  messageDirection: ext('message-direction'),
  agoraChannelId: ext('agora-channel-id'),
  callDurationSeconds: ext('call-duration-seconds'),
  callRecordingUrl: ext('call-recording-url'),
} as const;

// CareComm communication category system
export const CARECOMM_CATEGORY_SYSTEM = 'https://nuloucarepoint.com/fhir/CodeSystem/communication-category';

// Outcome vocabulary for call Communications. Every transition out of
// status=in-progress should set Communication.statusReason with one of
// these codes so we have an auditable trail of how each call ended.
export const CARECOMM_OUTCOME_SYSTEM = 'https://nuloucarepoint.com/fhir/CodeSystem/carecomm-outcome';

export const CARECOMM_OUTCOME = {
  completedNormally: 'completed-normally',
  declined: 'declined',
  abandonedProvider: 'abandoned-provider',
  abandonedPatient: 'abandoned-patient',
  autoTimeout: 'auto-timeout',
  error: 'error',
} as const;

export type CareCommOutcomeCode = typeof CARECOMM_OUTCOME[keyof typeof CARECOMM_OUTCOME];

export function carecommOutcomeReason(code: CareCommOutcomeCode, displayOverride?: string): { coding: [{ system: string; code: string; display: string }] } {
  const displayMap: Record<CareCommOutcomeCode, string> = {
    'completed-normally': 'Call completed normally',
    'declined': 'Declined by patient',
    'abandoned-provider': 'Abandoned (provider disconnect)',
    'abandoned-patient': 'Abandoned (patient disconnect)',
    'auto-timeout': 'Auto-completed by server (stale)',
    'error': 'Call failed due to error',
  };
  return {
    coding: [
      {
        system: CARECOMM_OUTCOME_SYSTEM,
        code,
        display: displayOverride ?? displayMap[code],
      },
    ],
  };
}

// CareComm communication categories
export type CareCommChannel = 'chat' | 'sms' | 'whatsapp' | 'voice-call' | 'video-call';

export const CARECOMM_CHANNELS: { value: CareCommChannel; label: string }[] = [
  { value: 'chat', label: 'Internal Chat' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'voice-call', label: 'Voice Call' },
  { value: 'video-call', label: 'Video Call' },
];

// ============================================
// Twilio Configuration Scope (Hybrid Model)
// ============================================

/**
 * Twilio configuration scope for the tenant
 *
 * TENANT_WIDE: Single Twilio config shared across all organizations
 * - Simpler setup, faster onboarding
 * - All orgs share the same phone numbers and messaging service
 * - Requires defaultOrganizationId for inbound routing
 *
 * ORG_SCOPED: Per-organization Twilio configuration
 * - Each org has its own Twilio subaccount/credentials
 * - Separate phone number pools per org
 * - Better for multi-brand/multi-clinic tenants
 * - Requires number uniqueness across orgs
 */
export type TwilioConfigScope = 'TENANT_WIDE' | 'ORG_SCOPED';

/**
 * Routing strategy for inbound messages
 *
 * BY_TO_NUMBER: Route based on the "To" phone number (recommended)
 * BY_MESSAGING_SERVICE_SID: Route based on Twilio Messaging Service SID
 * BY_EXPLICIT_CONTEXT: Only for outbound; inbound still needs To-number matching
 */
export type TwilioRoutingStrategy = 'BY_TO_NUMBER' | 'BY_MESSAGING_SERVICE_SID' | 'BY_EXPLICIT_CONTEXT';

/**
 * Tenant-level Twilio configuration (stored in Project)
 * Note: This is for documentation/type reference. Actual storage is in Medplum Project secrets.
 */
export interface TenantTwilioConfig {
  /** Configuration scope */
  configScope: TwilioConfigScope;
  /** Routing strategy for inbound messages */
  routingStrategy: TwilioRoutingStrategy;
  /** SMS messaging enabled */
  smsEnabled: boolean;
  /** WhatsApp messaging enabled */
  whatsAppEnabled: boolean;
  /** Phone numbers owned by this tenant (E.164 format) */
  ownedNumbers: string[];
  /** Messaging Service SID (if using Twilio Messaging Services) */
  messagingServiceSid?: string;
}

/**
 * Organization-level Twilio configuration (for ORG_SCOPED mode)
 * Stored in Organization extensions
 */
export interface OrgTwilioConfig {
  /** Whether this org has Twilio configured */
  configured: boolean;
  /** Phone numbers assigned to this org (E.164 format) */
  ownedNumbers: string[];
  /** Messaging Service SID for this org */
  messagingServiceSid?: string;
  /** Default "from" number for this org */
  defaultFromNumber?: string;
  /** SMS enabled for this org */
  smsEnabled: boolean;
  /** WhatsApp enabled for this org */
  whatsAppEnabled: boolean;
  /** Business hours for this org (JSON string) */
  businessHours?: string;
}

/**
 * Get an extension value from an organization by URL
 */
export function getExtension(
  organization: Organization | undefined,
  url: string
): Extension | undefined {
  return organization?.extension?.find((ext) => ext.url === url);
}

/**
 * Get all extensions matching a URL pattern (for arrays of items)
 */
export function getExtensions(
  organization: Organization | undefined,
  url: string
): Extension[] {
  return organization?.extension?.filter((ext) => ext.url === url) || [];
}

/**
 * Get a nested extension value from a complex extension
 */
export function getNestedExtensionValue<T>(
  extension: Extension | undefined,
  nestedUrl: string,
  valueKey: keyof Extension = 'valueString'
): T | undefined {
  const nested = extension?.extension?.find((ext) => ext.url === nestedUrl);
  return nested?.[valueKey] as T | undefined;
}

/**
 * Set or update an extension on an organization
 * Returns a new organization object with the updated extension
 */
export function setExtension(
  organization: Organization,
  url: string,
  extension: Extension
): Organization {
  const existingExtensions = organization.extension?.filter((ext) => ext.url !== url) || [];

  return {
    ...organization,
    extension: [...existingExtensions, { ...extension, url }],
  };
}

/**
 * Set multiple extensions of the same type (for arrays like reminders)
 * Replaces all existing extensions with the given URL
 */
export function setExtensions(
  organization: Organization,
  url: string,
  extensions: Extension[]
): Organization {
  const existingExtensions = organization.extension?.filter((ext) => ext.url !== url) || [];
  const newExtensions = extensions.map((ext) => ({ ...ext, url }));

  return {
    ...organization,
    extension: [...existingExtensions, ...newExtensions],
  };
}

/**
 * Remove an extension from an organization by URL
 */
export function removeExtension(
  organization: Organization,
  url: string
): Organization {
  return {
    ...organization,
    extension: organization.extension?.filter((ext) => ext.url !== url) || [],
  };
}

// ============================================
// Appointment Settings Helpers
// ============================================

export interface AppointmentSettingsData {
  // General Settings
  defaultDuration: string;
  bufferTime: number;
  advanceBookingDays: number;
  minNoticeHours: number;
  defaultAppointmentType: string;
  // Booking Options
  allowOnlineBooking: boolean;
  allowWalkIns: boolean;
  requireConfirmation: boolean;
  // Reminder Settings
  autoNotifyUpcoming: boolean;
  weekendRemindersOnFriday: boolean;
  autoCancelOnReply: boolean;
  // Manual Reminders
  smsReminderTemplate: string;
  emailReminderTemplate: string;
  sendReminderOnBooking: boolean;
  bookingReminderChannel: string;
  bookingReminderTemplate: string;
  // Rescheduling & Cancellation
  allowRescheduling: boolean;
  rescheduleNoticeHours: number;
  allowCancellation: boolean;
  cancellationNoticeHours: number;
}

export interface AutomaticReminderData {
  id: string;
  channel: string;
  template: string;
  daysBefore: number;
}

export function getAppointmentSettings(organization: Organization | undefined): AppointmentSettingsData | undefined {
  const ext = getExtension(organization, EXTENSION_URLS.appointmentSettings);
  if (!ext?.extension) return undefined;

  return {
    defaultDuration: getNestedExtensionValue<string>(ext, 'defaultDuration', 'valueString') || '30',
    bufferTime: getNestedExtensionValue<number>(ext, 'bufferTime', 'valueInteger') || 5,
    advanceBookingDays: getNestedExtensionValue<number>(ext, 'advanceBookingDays', 'valueInteger') || 30,
    minNoticeHours: getNestedExtensionValue<number>(ext, 'minNoticeHours', 'valueInteger') || 24,
    defaultAppointmentType: getNestedExtensionValue<string>(ext, 'defaultAppointmentType', 'valueString') || 'in-person',
    allowOnlineBooking: getNestedExtensionValue<boolean>(ext, 'allowOnlineBooking', 'valueBoolean') ?? true,
    allowWalkIns: getNestedExtensionValue<boolean>(ext, 'allowWalkIns', 'valueBoolean') ?? false,
    requireConfirmation: getNestedExtensionValue<boolean>(ext, 'requireConfirmation', 'valueBoolean') ?? true,
    autoNotifyUpcoming: getNestedExtensionValue<boolean>(ext, 'autoNotifyUpcoming', 'valueBoolean') ?? true,
    weekendRemindersOnFriday: getNestedExtensionValue<boolean>(ext, 'weekendRemindersOnFriday', 'valueBoolean') ?? false,
    autoCancelOnReply: getNestedExtensionValue<boolean>(ext, 'autoCancelOnReply', 'valueBoolean') ?? true,
    smsReminderTemplate: getNestedExtensionValue<string>(ext, 'smsReminderTemplate', 'valueString') || '',
    emailReminderTemplate: getNestedExtensionValue<string>(ext, 'emailReminderTemplate', 'valueString') || '',
    sendReminderOnBooking: getNestedExtensionValue<boolean>(ext, 'sendReminderOnBooking', 'valueBoolean') ?? true,
    bookingReminderChannel: getNestedExtensionValue<string>(ext, 'bookingReminderChannel', 'valueString') || 'email',
    bookingReminderTemplate: getNestedExtensionValue<string>(ext, 'bookingReminderTemplate', 'valueString') || 'appointment-reminder',
    allowRescheduling: getNestedExtensionValue<boolean>(ext, 'allowRescheduling', 'valueBoolean') ?? true,
    rescheduleNoticeHours: getNestedExtensionValue<number>(ext, 'rescheduleNoticeHours', 'valueInteger') || 24,
    allowCancellation: getNestedExtensionValue<boolean>(ext, 'allowCancellation', 'valueBoolean') ?? true,
    cancellationNoticeHours: getNestedExtensionValue<number>(ext, 'cancellationNoticeHours', 'valueInteger') || 24,
  };
}

export function buildAppointmentSettingsExtension(settings: AppointmentSettingsData): Extension {
  return {
    url: EXTENSION_URLS.appointmentSettings,
    extension: [
      { url: 'defaultDuration', valueString: settings.defaultDuration },
      { url: 'bufferTime', valueInteger: settings.bufferTime },
      { url: 'advanceBookingDays', valueInteger: settings.advanceBookingDays },
      { url: 'minNoticeHours', valueInteger: settings.minNoticeHours },
      { url: 'defaultAppointmentType', valueString: settings.defaultAppointmentType },
      { url: 'allowOnlineBooking', valueBoolean: settings.allowOnlineBooking },
      { url: 'allowWalkIns', valueBoolean: settings.allowWalkIns },
      { url: 'requireConfirmation', valueBoolean: settings.requireConfirmation },
      { url: 'autoNotifyUpcoming', valueBoolean: settings.autoNotifyUpcoming },
      { url: 'weekendRemindersOnFriday', valueBoolean: settings.weekendRemindersOnFriday },
      { url: 'autoCancelOnReply', valueBoolean: settings.autoCancelOnReply },
      { url: 'smsReminderTemplate', valueString: settings.smsReminderTemplate },
      { url: 'emailReminderTemplate', valueString: settings.emailReminderTemplate },
      { url: 'sendReminderOnBooking', valueBoolean: settings.sendReminderOnBooking },
      { url: 'bookingReminderChannel', valueString: settings.bookingReminderChannel },
      { url: 'bookingReminderTemplate', valueString: settings.bookingReminderTemplate },
      { url: 'allowRescheduling', valueBoolean: settings.allowRescheduling },
      { url: 'rescheduleNoticeHours', valueInteger: settings.rescheduleNoticeHours },
      { url: 'allowCancellation', valueBoolean: settings.allowCancellation },
      { url: 'cancellationNoticeHours', valueInteger: settings.cancellationNoticeHours },
    ],
  };
}

export function getAutomaticReminders(organization: Organization | undefined): AutomaticReminderData[] {
  const extensions = getExtensions(organization, EXTENSION_URLS.automaticReminders);
  return extensions.map((ext) => ({
    id: getNestedExtensionValue<string>(ext, 'id', 'valueString') || Date.now().toString(),
    channel: getNestedExtensionValue<string>(ext, 'channel', 'valueString') || 'email',
    template: getNestedExtensionValue<string>(ext, 'template', 'valueString') || 'appointment-reminder',
    daysBefore: getNestedExtensionValue<number>(ext, 'daysBefore', 'valueInteger') || 1,
  }));
}

export function buildAutomaticReminderExtensions(reminders: AutomaticReminderData[]): Extension[] {
  return reminders.map((reminder) => ({
    url: EXTENSION_URLS.automaticReminders,
    extension: [
      { url: 'id', valueString: reminder.id },
      { url: 'channel', valueString: reminder.channel },
      { url: 'template', valueString: reminder.template },
      { url: 'daysBefore', valueInteger: reminder.daysBefore },
    ],
  }));
}

// ============================================
// Working Hours Settings Helpers
// ============================================

export interface DayScheduleData {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

export interface WorkingHoursData {
  expectedProductiveTime: number;
  workingDays: {
    monday: DayScheduleData;
    tuesday: DayScheduleData;
    wednesday: DayScheduleData;
    thursday: DayScheduleData;
    friday: DayScheduleData;
    saturday: DayScheduleData;
    sunday: DayScheduleData;
  };
  lunchDuration: string;
  lunchTime: string;
}

export interface BreakHourData {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

export function getWorkingHoursSettings(organization: Organization | undefined): WorkingHoursData | undefined {
  const ext = getExtension(organization, EXTENSION_URLS.workingHoursSettings);
  if (!ext?.extension) return undefined;

  const workingDays = {} as WorkingHoursData['workingDays'];

  for (const day of DAYS) {
    const dayExt = ext.extension?.find((e) => e.url === day);
    workingDays[day] = {
      enabled: getNestedExtensionValue<boolean>(dayExt, 'enabled', 'valueBoolean') ?? (day !== 'saturday' && day !== 'sunday'),
      startTime: getNestedExtensionValue<string>(dayExt, 'startTime', 'valueString') || '09:30',
      endTime: getNestedExtensionValue<string>(dayExt, 'endTime', 'valueString') || '17:30',
    };
  }

  return {
    expectedProductiveTime: getNestedExtensionValue<number>(ext, 'expectedProductiveTime', 'valueInteger') || 8,
    workingDays,
    lunchDuration: getNestedExtensionValue<string>(ext, 'lunchDuration', 'valueString') || '45',
    lunchTime: getNestedExtensionValue<string>(ext, 'lunchTime', 'valueString') || '13:00',
  };
}

export function buildWorkingHoursExtension(settings: WorkingHoursData): Extension {
  const dayExtensions = DAYS.map((day) => ({
    url: day,
    extension: [
      { url: 'enabled', valueBoolean: settings.workingDays[day].enabled },
      { url: 'startTime', valueString: settings.workingDays[day].startTime },
      { url: 'endTime', valueString: settings.workingDays[day].endTime },
    ],
  }));

  return {
    url: EXTENSION_URLS.workingHoursSettings,
    extension: [
      { url: 'expectedProductiveTime', valueInteger: settings.expectedProductiveTime },
      ...dayExtensions,
      { url: 'lunchDuration', valueString: settings.lunchDuration },
      { url: 'lunchTime', valueString: settings.lunchTime },
    ],
  };
}

export function getBreakHours(organization: Organization | undefined): BreakHourData[] {
  const extensions = getExtensions(organization, EXTENSION_URLS.breakHours);
  return extensions.map((ext) => ({
    id: getNestedExtensionValue<string>(ext, 'id', 'valueString') || Date.now().toString(),
    name: getNestedExtensionValue<string>(ext, 'name', 'valueString') || 'Break',
    startTime: getNestedExtensionValue<string>(ext, 'startTime', 'valueString') || '10:00',
    endTime: getNestedExtensionValue<string>(ext, 'endTime', 'valueString') || '10:15',
  }));
}

export function buildBreakHourExtensions(breaks: BreakHourData[]): Extension[] {
  return breaks.map((breakItem) => ({
    url: EXTENSION_URLS.breakHours,
    extension: [
      { url: 'id', valueString: breakItem.id },
      { url: 'name', valueString: breakItem.name },
      { url: 'startTime', valueString: breakItem.startTime },
      { url: 'endTime', valueString: breakItem.endTime },
    ],
  }));
}

// ============================================
// Cancellation Reasons Helpers
// ============================================

export interface CancellationReasonData {
  id: string;
  name: string;
  description: string;
  requiresNote: boolean;
  chargesFee: boolean;
  feePercentage: number;
  isActive: boolean;
}

export function getCancellationReasons(organization: Organization | undefined): CancellationReasonData[] {
  const extensions = getExtensions(organization, EXTENSION_URLS.cancellationReasons);
  return extensions.map((ext) => ({
    id: getNestedExtensionValue<string>(ext, 'id', 'valueString') || Date.now().toString(),
    name: getNestedExtensionValue<string>(ext, 'name', 'valueString') || '',
    description: getNestedExtensionValue<string>(ext, 'description', 'valueString') || '',
    requiresNote: getNestedExtensionValue<boolean>(ext, 'requiresNote', 'valueBoolean') ?? false,
    chargesFee: getNestedExtensionValue<boolean>(ext, 'chargesFee', 'valueBoolean') ?? false,
    feePercentage: getNestedExtensionValue<number>(ext, 'feePercentage', 'valueInteger') || 0,
    isActive: getNestedExtensionValue<boolean>(ext, 'isActive', 'valueBoolean') ?? true,
  }));
}

export function buildCancellationReasonExtensions(reasons: CancellationReasonData[]): Extension[] {
  return reasons.map((reason) => ({
    url: EXTENSION_URLS.cancellationReasons,
    extension: [
      { url: 'id', valueString: reason.id },
      { url: 'name', valueString: reason.name },
      { url: 'description', valueString: reason.description },
      { url: 'requiresNote', valueBoolean: reason.requiresNote },
      { url: 'chargesFee', valueBoolean: reason.chargesFee },
      { url: 'feePercentage', valueInteger: reason.feePercentage },
      { url: 'isActive', valueBoolean: reason.isActive },
    ],
  }));
}

// ============================================
// Email Settings Helpers
// ============================================

/**
 * Email settings stored in FHIR Organization extensions
 * NOTE: Password is NOT stored here for security reasons.
 * It should be stored in Medplum Project secrets.
 */
export interface EmailSettingsData {
  provider: string;
  smtpHost: string;
  smtpPort: string;
  username: string;
  password: string; // Not stored in FHIR - UI only
  fromEmail: string;
  fromName: string;
  encryption: string;
  enabled: boolean;
  // Flag indicating if password is configured (stored in Project secrets)
  passwordConfigured?: boolean;
}

export function getEmailSettings(organization: Organization | undefined): EmailSettingsData | undefined {
  const ext = getExtension(organization, EXTENSION_URLS.emailSettings);
  if (!ext?.extension) return undefined;

  return {
    provider: getNestedExtensionValue<string>(ext, 'provider', 'valueString') || 'smtp',
    smtpHost: getNestedExtensionValue<string>(ext, 'smtpHost', 'valueString') || '',
    smtpPort: getNestedExtensionValue<string>(ext, 'smtpPort', 'valueString') || '587',
    username: getNestedExtensionValue<string>(ext, 'username', 'valueString') || '',
    password: '', // Never loaded from FHIR - secrets are in Project resource
    fromEmail: getNestedExtensionValue<string>(ext, 'fromEmail', 'valueString') || '',
    fromName: getNestedExtensionValue<string>(ext, 'fromName', 'valueString') || '',
    encryption: getNestedExtensionValue<string>(ext, 'encryption', 'valueString') || 'tls',
    enabled: getNestedExtensionValue<boolean>(ext, 'enabled', 'valueBoolean') ?? false,
    passwordConfigured: getNestedExtensionValue<boolean>(ext, 'passwordConfigured', 'valueBoolean') ?? false,
  };
}

/**
 * Build email settings extension for FHIR storage
 * NOTE: Password is excluded - only a "configured" flag is stored
 */
export function buildEmailSettingsExtension(settings: EmailSettingsData): Extension {
  return {
    url: EXTENSION_URLS.emailSettings,
    extension: [
      { url: 'provider', valueString: settings.provider },
      { url: 'smtpHost', valueString: settings.smtpHost },
      { url: 'smtpPort', valueString: settings.smtpPort },
      { url: 'username', valueString: settings.username },
      // Password is NOT stored in FHIR - only a flag indicating it's configured
      { url: 'passwordConfigured', valueBoolean: settings.passwordConfigured ?? false },
      { url: 'fromEmail', valueString: settings.fromEmail },
      { url: 'fromName', valueString: settings.fromName },
      { url: 'encryption', valueString: settings.encryption },
      { url: 'enabled', valueBoolean: settings.enabled },
    ],
  };
}

// ============================================
// SMS Settings Helpers
// ============================================

/**
 * SMS settings stored in FHIR Organization extensions
 * NOTE: accountSid and authToken are NOT stored here for security reasons.
 * They should be stored in Medplum Project secrets.
 */
export interface SMSSettingsData {
  provider: string;
  accountSid: string; // Not stored in FHIR - UI only
  authToken: string; // Not stored in FHIR - UI only
  fromNumber: string;
  enabled: boolean;
  // Flags indicating if credentials are configured (stored in Project secrets)
  credentialsConfigured?: boolean;
}

export function getSMSSettings(organization: Organization | undefined): SMSSettingsData | undefined {
  const ext = getExtension(organization, EXTENSION_URLS.smsSettings);
  if (!ext?.extension) return undefined;

  return {
    provider: getNestedExtensionValue<string>(ext, 'provider', 'valueString') || 'twilio',
    accountSid: '', // Never loaded from FHIR - secrets are in Project resource
    authToken: '', // Never loaded from FHIR - secrets are in Project resource
    fromNumber: getNestedExtensionValue<string>(ext, 'fromNumber', 'valueString') || '',
    enabled: getNestedExtensionValue<boolean>(ext, 'enabled', 'valueBoolean') ?? false,
    credentialsConfigured: getNestedExtensionValue<boolean>(ext, 'credentialsConfigured', 'valueBoolean') ?? false,
  };
}

/**
 * Build SMS settings extension for FHIR storage
 * NOTE: Credentials are excluded - only a "configured" flag is stored
 */
export function buildSMSSettingsExtension(settings: SMSSettingsData): Extension {
  return {
    url: EXTENSION_URLS.smsSettings,
    extension: [
      { url: 'provider', valueString: settings.provider },
      // accountSid and authToken are NOT stored in FHIR - only a flag
      { url: 'credentialsConfigured', valueBoolean: settings.credentialsConfigured ?? false },
      { url: 'fromNumber', valueString: settings.fromNumber },
      { url: 'enabled', valueBoolean: settings.enabled },
    ],
  };
}

// ============================================
// GDPR Settings Helpers
// ============================================

export interface GDPRSettingsData {
  cookieBannerEnabled: boolean;
  cookieBannerText: string;
  privacyPolicyUrl: string;
  termsOfServiceUrl: string;
  dataRetentionDays: string;
  allowDataExport: boolean;
  allowDataDeletion: boolean;
  requireConsentForMarketing: boolean;
  requireConsentForAnalytics: boolean;
}

export function getGDPRSettings(organization: Organization | undefined): GDPRSettingsData | undefined {
  const ext = getExtension(organization, EXTENSION_URLS.gdprSettings);
  if (!ext?.extension) return undefined;

  return {
    cookieBannerEnabled: getNestedExtensionValue<boolean>(ext, 'cookieBannerEnabled', 'valueBoolean') ?? true,
    cookieBannerText: getNestedExtensionValue<string>(ext, 'cookieBannerText', 'valueString') || '',
    privacyPolicyUrl: getNestedExtensionValue<string>(ext, 'privacyPolicyUrl', 'valueString') || '',
    termsOfServiceUrl: getNestedExtensionValue<string>(ext, 'termsOfServiceUrl', 'valueString') || '',
    dataRetentionDays: getNestedExtensionValue<string>(ext, 'dataRetentionDays', 'valueString') || '365',
    allowDataExport: getNestedExtensionValue<boolean>(ext, 'allowDataExport', 'valueBoolean') ?? true,
    allowDataDeletion: getNestedExtensionValue<boolean>(ext, 'allowDataDeletion', 'valueBoolean') ?? true,
    requireConsentForMarketing: getNestedExtensionValue<boolean>(ext, 'requireConsentForMarketing', 'valueBoolean') ?? true,
    requireConsentForAnalytics: getNestedExtensionValue<boolean>(ext, 'requireConsentForAnalytics', 'valueBoolean') ?? true,
  };
}

export function buildGDPRSettingsExtension(settings: GDPRSettingsData): Extension {
  return {
    url: EXTENSION_URLS.gdprSettings,
    extension: [
      { url: 'cookieBannerEnabled', valueBoolean: settings.cookieBannerEnabled },
      { url: 'cookieBannerText', valueString: settings.cookieBannerText },
      { url: 'privacyPolicyUrl', valueString: settings.privacyPolicyUrl },
      { url: 'termsOfServiceUrl', valueString: settings.termsOfServiceUrl },
      { url: 'dataRetentionDays', valueString: settings.dataRetentionDays },
      { url: 'allowDataExport', valueBoolean: settings.allowDataExport },
      { url: 'allowDataDeletion', valueBoolean: settings.allowDataDeletion },
      { url: 'requireConsentForMarketing', valueBoolean: settings.requireConsentForMarketing },
      { url: 'requireConsentForAnalytics', valueBoolean: settings.requireConsentForAnalytics },
    ],
  };
}

// ============================================
// Finance Settings Helpers
// ============================================

/**
 * Finance settings stored in FHIR Organization extensions
 * NOTE: stripeSecretKey is NOT stored here for security reasons.
 * It should be stored in Medplum Project secrets.
 * stripePublicKey is safe to store as it's meant to be public.
 */
export interface FinanceSettingsData {
  currency: string;
  taxRate: string;
  taxId: string;
  paymentGateway: string;
  stripePublicKey: string; // Safe to store - this is a publishable key
  stripeSecretKey: string; // Not stored in FHIR - UI only
  enableOnlinePayments: boolean;
  enableInvoicing: boolean;
  invoicePrefix: string;
  invoiceStartNumber: string;
  paymentTermsDays: string;
  // Flag indicating if secret key is configured (stored in Project secrets)
  stripeSecretKeyConfigured?: boolean;
}

export function getFinanceSettings(organization: Organization | undefined): FinanceSettingsData | undefined {
  const financeExt = getExtension(organization, EXTENSION_URLS.financeSettings);
  if (!financeExt?.extension) return undefined;

  return {
    currency: getNestedExtensionValue<string>(financeExt, 'currency', 'valueString') || 'USD',
    taxRate: getNestedExtensionValue<string>(financeExt, 'taxRate', 'valueString') || '0',
    taxId: getNestedExtensionValue<string>(financeExt, 'taxId', 'valueString') || '',
    paymentGateway: getNestedExtensionValue<string>(financeExt, 'paymentGateway', 'valueString') || 'stripe',
    stripePublicKey: getNestedExtensionValue<string>(financeExt, 'stripePublicKey', 'valueString') || '',
    stripeSecretKey: '', // Never loaded from FHIR - secrets are in Project resource
    enableOnlinePayments: getNestedExtensionValue<boolean>(financeExt, 'enableOnlinePayments', 'valueBoolean') ?? false,
    enableInvoicing: getNestedExtensionValue<boolean>(financeExt, 'enableInvoicing', 'valueBoolean') ?? true,
    invoicePrefix: getNestedExtensionValue<string>(financeExt, 'invoicePrefix', 'valueString') || 'INV-',
    invoiceStartNumber: getNestedExtensionValue<string>(financeExt, 'invoiceStartNumber', 'valueString') || '1001',
    paymentTermsDays: getNestedExtensionValue<string>(financeExt, 'paymentTermsDays', 'valueString') || '30',
    stripeSecretKeyConfigured: getNestedExtensionValue<boolean>(financeExt, 'stripeSecretKeyConfigured', 'valueBoolean') ?? false,
  };
}

/**
 * Build finance settings extension for FHIR storage
 * NOTE: stripeSecretKey is excluded - only a "configured" flag is stored
 */
export function buildFinanceSettingsExtension(settings: FinanceSettingsData): Extension {
  return {
    url: EXTENSION_URLS.financeSettings,
    extension: [
      { url: 'currency', valueString: settings.currency },
      { url: 'taxRate', valueString: settings.taxRate },
      { url: 'taxId', valueString: settings.taxId },
      { url: 'paymentGateway', valueString: settings.paymentGateway },
      { url: 'stripePublicKey', valueString: settings.stripePublicKey },
      // stripeSecretKey is NOT stored in FHIR - only a flag
      { url: 'stripeSecretKeyConfigured', valueBoolean: settings.stripeSecretKeyConfigured ?? false },
      { url: 'enableOnlinePayments', valueBoolean: settings.enableOnlinePayments },
      { url: 'enableInvoicing', valueBoolean: settings.enableInvoicing },
      { url: 'invoicePrefix', valueString: settings.invoicePrefix },
      { url: 'invoiceStartNumber', valueString: settings.invoiceStartNumber },
      { url: 'paymentTermsDays', valueString: settings.paymentTermsDays },
    ],
  };
}

// ============================================
// App Settings Helpers (User Preferences)
// ============================================

export interface AppSettingsData {
  language: string;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  darkModeDefault: boolean;
  showPatientPhotos: boolean;
  enableNotificationSounds: boolean;
  autoLogoutMinutes: string;
  defaultCalendarView: string;
  appointmentDuration: string;
}

export function getAppSettings(organization: Organization | undefined): AppSettingsData | undefined {
  const appExt = getExtension(organization, EXTENSION_URLS.appSettings);
  if (!appExt?.extension) return undefined;

  return {
    language: getNestedExtensionValue<string>(appExt, 'language', 'valueString') || 'en',
    timezone: getNestedExtensionValue<string>(appExt, 'timezone', 'valueString') || 'America/New_York',
    dateFormat: getNestedExtensionValue<string>(appExt, 'dateFormat', 'valueString') || 'MM/DD/YYYY',
    timeFormat: getNestedExtensionValue<string>(appExt, 'timeFormat', 'valueString') || '12h',
    darkModeDefault: getNestedExtensionValue<boolean>(appExt, 'darkModeDefault', 'valueBoolean') ?? false,
    showPatientPhotos: getNestedExtensionValue<boolean>(appExt, 'showPatientPhotos', 'valueBoolean') ?? true,
    enableNotificationSounds: getNestedExtensionValue<boolean>(appExt, 'enableNotificationSounds', 'valueBoolean') ?? true,
    autoLogoutMinutes: getNestedExtensionValue<string>(appExt, 'autoLogoutMinutes', 'valueString') || '30',
    defaultCalendarView: getNestedExtensionValue<string>(appExt, 'defaultCalendarView', 'valueString') || 'week',
    appointmentDuration: getNestedExtensionValue<string>(appExt, 'appointmentDuration', 'valueString') || '30',
  };
}

export function buildAppSettingsExtension(settings: AppSettingsData): Extension {
  return {
    url: EXTENSION_URLS.appSettings,
    extension: [
      { url: 'language', valueString: settings.language },
      { url: 'timezone', valueString: settings.timezone },
      { url: 'dateFormat', valueString: settings.dateFormat },
      { url: 'timeFormat', valueString: settings.timeFormat },
      { url: 'darkModeDefault', valueBoolean: settings.darkModeDefault },
      { url: 'showPatientPhotos', valueBoolean: settings.showPatientPhotos },
      { url: 'enableNotificationSounds', valueBoolean: settings.enableNotificationSounds },
      { url: 'autoLogoutMinutes', valueString: settings.autoLogoutMinutes },
      { url: 'defaultCalendarView', valueString: settings.defaultCalendarView },
      { url: 'appointmentDuration', valueString: settings.appointmentDuration },
    ],
  };
}

// ============================================
// Email Templates Helpers
// ============================================

export interface EmailTemplateData {
  id: string;
  name: string;
  subject: string;
  body: string;
  type: string;
  active: boolean;
}

export function getEmailTemplates(organization: Organization | undefined): EmailTemplateData[] {
  const extensions = getExtensions(organization, EXTENSION_URLS.emailTemplates);
  return extensions.map((emailExt) => ({
    id: getNestedExtensionValue<string>(emailExt, 'id', 'valueString') || Date.now().toString(),
    name: getNestedExtensionValue<string>(emailExt, 'name', 'valueString') || '',
    subject: getNestedExtensionValue<string>(emailExt, 'subject', 'valueString') || '',
    body: getNestedExtensionValue<string>(emailExt, 'body', 'valueString') || '',
    type: getNestedExtensionValue<string>(emailExt, 'type', 'valueString') || 'custom',
    active: getNestedExtensionValue<boolean>(emailExt, 'active', 'valueBoolean') ?? true,
  }));
}

export function buildEmailTemplateExtensions(templates: EmailTemplateData[]): Extension[] {
  return templates.map((template) => ({
    url: EXTENSION_URLS.emailTemplates,
    extension: [
      { url: 'id', valueString: template.id },
      { url: 'name', valueString: template.name },
      { url: 'subject', valueString: template.subject },
      { url: 'body', valueString: template.body },
      { url: 'type', valueString: template.type },
      { url: 'active', valueBoolean: template.active },
    ],
  }));
}

// ============================================
// SMS Templates Helpers
// ============================================

export interface SMSTemplateData {
  id: string;
  name: string;
  message: string;
  type: string;
  active: boolean;
}

export function getSMSTemplates(organization: Organization | undefined): SMSTemplateData[] {
  const extensions = getExtensions(organization, EXTENSION_URLS.smsTemplates);
  return extensions.map((smsExt) => ({
    id: getNestedExtensionValue<string>(smsExt, 'id', 'valueString') || Date.now().toString(),
    name: getNestedExtensionValue<string>(smsExt, 'name', 'valueString') || '',
    message: getNestedExtensionValue<string>(smsExt, 'message', 'valueString') || '',
    type: getNestedExtensionValue<string>(smsExt, 'type', 'valueString') || 'custom',
    active: getNestedExtensionValue<boolean>(smsExt, 'active', 'valueBoolean') ?? true,
  }));
}

export function buildSMSTemplateExtensions(templates: SMSTemplateData[]): Extension[] {
  return templates.map((template) => ({
    url: EXTENSION_URLS.smsTemplates,
    extension: [
      { url: 'id', valueString: template.id },
      { url: 'name', valueString: template.name },
      { url: 'message', valueString: template.message },
      { url: 'type', valueString: template.type },
      { url: 'active', valueBoolean: template.active },
    ],
  }));
}

// ============================================
// Measurement Settings Helpers (Metric/Imperial)
// ============================================

export type MeasurementSystem = 'metric' | 'imperial';
export type WeightUnit = 'kg' | 'lb';
export type HeightUnit = 'cm' | 'in' | 'ft-in';
export type TemperatureUnit = 'C' | 'F';

/**
 * Measurement settings for the practice
 * Following FHIR/Health IT best practices:
 * - All data is stored in metric units (UCUM) in FHIR
 * - This setting controls display/input preferences only
 * - Conversions happen in the application layer
 */
export interface MeasurementSettingsData {
  /** The overall measurement system preference */
  system: MeasurementSystem;
  /** Weight display unit */
  weightUnit: WeightUnit;
  /** Height display unit */
  heightUnit: HeightUnit;
  /** Temperature display unit */
  temperatureUnit: TemperatureUnit;
}

/** Default measurement settings (US healthcare defaults to imperial) */
export const DEFAULT_MEASUREMENT_SETTINGS: MeasurementSettingsData = {
  system: 'imperial',
  weightUnit: 'lb',
  heightUnit: 'ft-in',
  temperatureUnit: 'F',
};

/** Metric measurement settings */
export const METRIC_MEASUREMENT_SETTINGS: MeasurementSettingsData = {
  system: 'metric',
  weightUnit: 'kg',
  heightUnit: 'cm',
  temperatureUnit: 'C',
};

/** Imperial measurement settings */
export const IMPERIAL_MEASUREMENT_SETTINGS: MeasurementSettingsData = {
  system: 'imperial',
  weightUnit: 'lb',
  heightUnit: 'ft-in',
  temperatureUnit: 'F',
};

/**
 * Get measurement settings from an organization
 * Returns default imperial settings if not configured
 */
export function getMeasurementSettings(organization: Organization | undefined): MeasurementSettingsData {
  const ext = getExtension(organization, EXTENSION_URLS.measurementSettings);
  if (!ext?.extension) return DEFAULT_MEASUREMENT_SETTINGS;

  return {
    system: (getNestedExtensionValue<string>(ext, 'system', 'valueString') as MeasurementSystem) || 'imperial',
    weightUnit: (getNestedExtensionValue<string>(ext, 'weightUnit', 'valueString') as WeightUnit) || 'lb',
    heightUnit: (getNestedExtensionValue<string>(ext, 'heightUnit', 'valueString') as HeightUnit) || 'ft-in',
    temperatureUnit: (getNestedExtensionValue<string>(ext, 'temperatureUnit', 'valueString') as TemperatureUnit) || 'F',
  };
}

/**
 * Build measurement settings extension for FHIR storage
 */
export function buildMeasurementSettingsExtension(settings: MeasurementSettingsData): Extension {
  return {
    url: EXTENSION_URLS.measurementSettings,
    extension: [
      { url: 'system', valueString: settings.system },
      { url: 'weightUnit', valueString: settings.weightUnit },
      { url: 'heightUnit', valueString: settings.heightUnit },
      { url: 'temperatureUnit', valueString: settings.temperatureUnit },
    ],
  };
}

/**
 * Get the appropriate settings based on system preference
 */
export function getSettingsForSystem(system: MeasurementSystem): MeasurementSettingsData {
  return system === 'metric' ? METRIC_MEASUREMENT_SETTINGS : IMPERIAL_MEASUREMENT_SETTINGS;
}

// ============================================
// AI Notes Settings Helpers
// ============================================

/**
 * AWS Transcribe Medical specialty types
 * @see https://docs.aws.amazon.com/transcribe/latest/dg/transcribe-medical-specialties.html
 */
export type MedicalSpecialty =
  | 'PRIMARYCARE'
  | 'CARDIOLOGY'
  | 'NEUROLOGY'
  | 'ONCOLOGY'
  | 'RADIOLOGY'
  | 'UROLOGY';

/**
 * Transcription mode options
 * - streaming: Real-time transcription (higher cost, immediate feedback)
 * - batch: Post-recording transcription (lower cost, processes after recording ends)
 */
export type TranscriptionMode = 'streaming' | 'batch';

/**
 * Note generation output format
 */
export type NoteOutputFormat = 'soap' | 'progress' | 'custom';

/**
 * AI Notes settings stored in FHIR Organization extensions
 */
export interface AINotesSettingsData {
  /** Whether AI Notes feature is enabled for this practice */
  enabled: boolean;
  /** Medical specialty for AWS Transcribe Medical */
  medicalSpecialty: MedicalSpecialty;
  /** Transcription mode: streaming (real-time) or batch (post-recording) */
  transcriptionMode: TranscriptionMode;
  /** Preferred note output format */
  noteOutputFormat: NoteOutputFormat;
  /** Language code (default: en-US) */
  languageCode: string;
  /** Whether to enable speaker identification (diarization) */
  enableSpeakerIdentification: boolean;
  /** Maximum recording duration in minutes */
  maxRecordingMinutes: number;
  /** Whether to automatically extract clinical entities after transcription */
  autoExtractEntities: boolean;
  /** Custom vocabulary name (if configured in AWS) */
  customVocabularyName: string;
}

/** Default AI Notes settings */
export const DEFAULT_AI_NOTES_SETTINGS: AINotesSettingsData = {
  enabled: false,
  medicalSpecialty: 'PRIMARYCARE',
  transcriptionMode: 'batch',
  noteOutputFormat: 'soap',
  languageCode: 'en-US',
  enableSpeakerIdentification: true,
  maxRecordingMinutes: 30,
  autoExtractEntities: true,
  customVocabularyName: '',
};

/**
 * Medical specialty options for UI select
 */
export const MEDICAL_SPECIALTY_OPTIONS = [
  { value: 'PRIMARYCARE', label: 'Primary Care' },
  { value: 'CARDIOLOGY', label: 'Cardiology' },
  { value: 'NEUROLOGY', label: 'Neurology' },
  { value: 'ONCOLOGY', label: 'Oncology' },
  { value: 'RADIOLOGY', label: 'Radiology' },
  { value: 'UROLOGY', label: 'Urology' },
] as const;

/**
 * Transcription mode options for UI select
 * Note: Only batch mode is currently supported. Streaming may be added in a future release.
 */
export const TRANSCRIPTION_MODE_OPTIONS = [
  {
    value: 'batch',
    label: 'Batch Processing',
    description: 'Audio is transcribed after recording ends.',
  },
] as const;

/**
 * Note output format options for UI select
 */
export const NOTE_OUTPUT_FORMAT_OPTIONS = [
  { value: 'soap', label: 'SOAP Note' },
  { value: 'progress', label: 'Progress Note' },
  { value: 'custom', label: 'Custom Template' },
] as const;

/**
 * Get AI Notes settings from an organization
 * Returns default settings if not configured
 */
export function getAINotesSettings(organization: Organization | undefined): AINotesSettingsData {
  const ext = getExtension(organization, EXTENSION_URLS.aiNotesSettings);
  if (!ext?.extension) return DEFAULT_AI_NOTES_SETTINGS;

  return {
    enabled: getNestedExtensionValue<boolean>(ext, 'enabled', 'valueBoolean') ?? false,
    medicalSpecialty: (getNestedExtensionValue<string>(ext, 'medicalSpecialty', 'valueString') as MedicalSpecialty) || 'PRIMARYCARE',
    transcriptionMode: (getNestedExtensionValue<string>(ext, 'transcriptionMode', 'valueString') as TranscriptionMode) || 'batch',
    noteOutputFormat: (getNestedExtensionValue<string>(ext, 'noteOutputFormat', 'valueString') as NoteOutputFormat) || 'soap',
    languageCode: getNestedExtensionValue<string>(ext, 'languageCode', 'valueString') || 'en-US',
    enableSpeakerIdentification: getNestedExtensionValue<boolean>(ext, 'enableSpeakerIdentification', 'valueBoolean') ?? true,
    maxRecordingMinutes: getNestedExtensionValue<number>(ext, 'maxRecordingMinutes', 'valueInteger') || 30,
    autoExtractEntities: getNestedExtensionValue<boolean>(ext, 'autoExtractEntities', 'valueBoolean') ?? true,
    customVocabularyName: getNestedExtensionValue<string>(ext, 'customVocabularyName', 'valueString') || '',
  };
}

/**
 * Build AI Notes settings extension for FHIR storage
 */
export function buildAINotesSettingsExtension(settings: AINotesSettingsData): Extension {
  const extensions: Extension[] = [
    { url: 'enabled', valueBoolean: settings.enabled },
    { url: 'medicalSpecialty', valueString: settings.medicalSpecialty },
    { url: 'transcriptionMode', valueString: settings.transcriptionMode },
    { url: 'noteOutputFormat', valueString: settings.noteOutputFormat },
    { url: 'languageCode', valueString: settings.languageCode },
    { url: 'enableSpeakerIdentification', valueBoolean: settings.enableSpeakerIdentification },
    { url: 'maxRecordingMinutes', valueInteger: settings.maxRecordingMinutes },
    { url: 'autoExtractEntities', valueBoolean: settings.autoExtractEntities },
  ];

  // Only include customVocabularyName if it has a value (empty strings are invalid in FHIR)
  if (settings.customVocabularyName) {
    extensions.push({ url: 'customVocabularyName', valueString: settings.customVocabularyName });
  }

  return {
    url: EXTENSION_URLS.aiNotesSettings,
    extension: extensions,
  };
}

// ============================================
// Form Settings Helpers
// ============================================

/**
 * Interface for individual form setting
 * Controls how a specific Questionnaire is configured for this organization
 */
export interface OrganizationFormSetting {
  /** Reference to the Questionnaire resource ID */
  questionnaireId: string;
  /** Whether form is available in this organization */
  enabled: boolean;
  /** Whether form is required for encounters */
  isRequired: boolean;
  /** Sort order for display (lower numbers appear first) */
  displayOrder: number;
  /** Optional override for form title */
  customTitle?: string;
}

/**
 * Interface for all form settings on an organization
 */
export interface OrganizationFormSettingsData {
  settings: OrganizationFormSetting[];
}

/**
 * Get form settings from organization
 * Returns all form configurations stored on the organization
 */
export function getFormSettings(organization: Organization | undefined): OrganizationFormSettingsData | undefined {
  const ext = getExtension(organization, EXTENSION_URLS.formSettings);
  if (!ext?.extension) return undefined;

  // Each "setting" nested extension contains one form's configuration
  const settingExtensions = ext.extension.filter((e) => e.url === 'setting');

  const settings: OrganizationFormSetting[] = settingExtensions.map((settingExt) => ({
    questionnaireId: getNestedExtensionValue<string>(settingExt, 'questionnaireId', 'valueString') || '',
    enabled: getNestedExtensionValue<boolean>(settingExt, 'enabled', 'valueBoolean') ?? true,
    isRequired: getNestedExtensionValue<boolean>(settingExt, 'isRequired', 'valueBoolean') ?? false,
    displayOrder: getNestedExtensionValue<number>(settingExt, 'displayOrder', 'valueInteger') ?? 0,
    customTitle: getNestedExtensionValue<string>(settingExt, 'customTitle', 'valueString') || undefined,
  }));

  return { settings };
}

/**
 * Get settings for a specific form by questionnaire ID
 */
export function getFormSetting(
  organization: Organization | undefined,
  questionnaireId: string
): OrganizationFormSetting | undefined {
  const formSettings = getFormSettings(organization);
  return formSettings?.settings.find((s) => s.questionnaireId === questionnaireId);
}

/**
 * Build form settings extension for FHIR storage
 */
export function buildFormSettingsExtension(data: OrganizationFormSettingsData): Extension {
  const settingExtensions = data.settings.map((setting) => {
    const extensions: Extension[] = [
      { url: 'questionnaireId', valueString: setting.questionnaireId },
      { url: 'enabled', valueBoolean: setting.enabled },
      { url: 'isRequired', valueBoolean: setting.isRequired },
      { url: 'displayOrder', valueInteger: setting.displayOrder },
    ];

    // Only include customTitle if it has a value
    if (setting.customTitle) {
      extensions.push({ url: 'customTitle', valueString: setting.customTitle });
    }

    return {
      url: 'setting',
      extension: extensions,
    };
  });

  return {
    url: EXTENSION_URLS.formSettings,
    extension: settingExtensions,
  };
}

// ============================================
// CareComm Settings Helpers
// ============================================

/**
 * CareComm settings stored in FHIR Organization extensions
 * Controls omnichannel communication features (SMS, WhatsApp, Voice, Video)
 */
export interface CareCommSettingsData {
  /** Whether CareComm module is enabled for this practice */
  enabled: boolean;
  /** SMS messaging enabled */
  smsEnabled: boolean;
  /** WhatsApp messaging enabled */
  whatsappEnabled: boolean;
  /** Voice calls enabled */
  voiceEnabled: boolean;
  /** Video calls enabled */
  videoEnabled: boolean;
  /** Whether Twilio credentials are configured */
  twilioConfigured: boolean;
  /** Whether Agora credentials are configured */
  agoraConfigured: boolean;
  /** Automatically create encounter for calls */
  autoCreateEncounter: boolean;
  /** Default greeting template */
  defaultGreeting: string;
  /** Enable call recording (if allowed by regional settings) */
  recordingEnabled: boolean;
}

/** Default CareComm settings */
export const DEFAULT_CARECOMM_SETTINGS: CareCommSettingsData = {
  enabled: false,
  smsEnabled: false,
  whatsappEnabled: false,
  voiceEnabled: false,
  videoEnabled: false,
  twilioConfigured: false,
  agoraConfigured: false,
  autoCreateEncounter: true,
  defaultGreeting: 'Hello, this is {practitionerName} from {organizationName}.',
  recordingEnabled: false,
};

/**
 * Get CareComm settings from an organization
 * Returns default settings if not configured
 */
export function getCareCommSettings(organization: Organization | undefined): CareCommSettingsData {
  const ext = getExtension(organization, EXTENSION_URLS.careCommSettings);
  if (!ext?.extension) return DEFAULT_CARECOMM_SETTINGS;

  return {
    enabled: getNestedExtensionValue<boolean>(ext, 'enabled', 'valueBoolean') ?? false,
    smsEnabled: getNestedExtensionValue<boolean>(ext, 'smsEnabled', 'valueBoolean') ?? false,
    whatsappEnabled: getNestedExtensionValue<boolean>(ext, 'whatsappEnabled', 'valueBoolean') ?? false,
    voiceEnabled: getNestedExtensionValue<boolean>(ext, 'voiceEnabled', 'valueBoolean') ?? false,
    videoEnabled: getNestedExtensionValue<boolean>(ext, 'videoEnabled', 'valueBoolean') ?? false,
    twilioConfigured: getNestedExtensionValue<boolean>(ext, 'twilioConfigured', 'valueBoolean') ?? false,
    agoraConfigured: getNestedExtensionValue<boolean>(ext, 'agoraConfigured', 'valueBoolean') ?? false,
    autoCreateEncounter: getNestedExtensionValue<boolean>(ext, 'autoCreateEncounter', 'valueBoolean') ?? true,
    defaultGreeting: getNestedExtensionValue<string>(ext, 'defaultGreeting', 'valueString') || DEFAULT_CARECOMM_SETTINGS.defaultGreeting,
    recordingEnabled: getNestedExtensionValue<boolean>(ext, 'recordingEnabled', 'valueBoolean') ?? false,
  };
}

/**
 * Build CareComm settings extension for FHIR storage
 */
export function buildCareCommSettingsExtension(settings: CareCommSettingsData): Extension {
  return {
    url: EXTENSION_URLS.careCommSettings,
    extension: [
      { url: 'enabled', valueBoolean: settings.enabled },
      { url: 'smsEnabled', valueBoolean: settings.smsEnabled },
      { url: 'whatsappEnabled', valueBoolean: settings.whatsappEnabled },
      { url: 'voiceEnabled', valueBoolean: settings.voiceEnabled },
      { url: 'videoEnabled', valueBoolean: settings.videoEnabled },
      { url: 'twilioConfigured', valueBoolean: settings.twilioConfigured },
      { url: 'agoraConfigured', valueBoolean: settings.agoraConfigured },
      { url: 'autoCreateEncounter', valueBoolean: settings.autoCreateEncounter },
      { url: 'defaultGreeting', valueString: settings.defaultGreeting },
      { url: 'recordingEnabled', valueBoolean: settings.recordingEnabled },
    ],
  };
}
